import { createElement } from "react";
import { createTheme } from "@mui/material/styles";
import Icon from "@shared/components/Icon";
import { attachmentStyled } from "./attachmentStyled";
import { glossaryStyled } from "./glossaryStyled";
import { infoBlockStyled } from "./infoBlockStyled";
import { scrollableTabsStyled } from "./scrollableTabsStyled";
import { searchBarStyled } from "./searchBarStyled";
import { textStyled } from "./textStyled";
import { timeLineStepStyled } from "./timeLineStepStyled";
const overrideKey = "customization";
const colors = {
    brandBlue: "#0000FF",
    brandBlueLight: "#1D23F5",
    brandBlueDark: "#0000CC",
    brandBlueSoft: "#84AAFF",
    inkMain: "#27272B",
    inkMuted: "#4F4F56",
    white: "#FFFFFF",
    black: "#000000",
    slateMain: "#282828",
    slateLight: "#2e2e2e",
    slateDark: "#1d1d1d",
    slateButton: "#282828",
    dangerLight: "#FF6B66",
    dangerMain: "#E32622",
    dangerDark: "#C81F1B",
    greenMain: "#0CFFC6",
    borderDefault: "#AAAAB6",
    borderDisabled: "#D8E0EA",
    textDisabled: "#C2CFDD",
    surfaceDefault: "#FFFFFF",
    surfaceSection: "#F9FBFF",
    surfaceAttachment: "#D1EBFF",
    surfaceAttachmentHover: "#9CD3FD",
    surfaceDangerSubtle: "#FFF6F6",
    surfacePrimaryHover: "#EEF1FF",
    surfacePrimaryActive: "#E2E7FF",
    surfaceDangerHover: "#FFF4F3",
    surfaceDangerActive: "#FFEAE9",
    surfaceInheritHover: "#F1F3F6",
    surfaceInheritActive: "#E9EDF2",
    surfaceGrey: "#F6F6F9",
    shadowPrimary: "rgba(28, 39, 64, 0.08)",
    additionalYellow: "#FFFF80",
    additionalTurquoise: "#0CFFC6",
    additionalViolet: "#BF7CFF",
    additionalOrange: "#FF5657",
};
const primary = {
    light: colors.brandBlueLight,
    main: colors.brandBlue,
    dark: colors.brandBlueDark,
    text: "#282828",
    contrastText: colors.white,
};
const secondary = {
    light: colors.slateLight,
    main: colors.slateMain,
    dark: colors.slateDark,
    contrastText: colors.white,
    text: colors.white,
    btnBackground: colors.slateButton,
};
const error = {
    light: "#FFEDEB",
    main: "#D91414",
    dark: "#A70E0E",
    contrastText: colors.white,
};
const success = {
    light: "#E2F6E2",
    main: "#007F00",
    dark: "#005A00",
    contrastText: colors.white,
};
const warning = {
    light: "#FFEEDD",
    main: "#C24100",
    dark: "#8A2F00",
    contrastText: colors.white,
};
const info = {
    light: "#EAF5FF",
    main: "#006BD4",
    dark: "#004EAA",
    contrastText: colors.white,
};
const border = {
    default: colors.borderDefault,
    soft: colors.brandBlueSoft,
    active: colors.greenMain,
    grey: "#E2E2E9",
    disabled: colors.borderDisabled,
    separator: colors.inkMain,
};
const additional = {
    yellow: colors.additionalYellow,
    turquoise: colors.additionalTurquoise,
    violet: colors.additionalViolet,
    orange: colors.additionalOrange,
};
const common = {
    consent: {
        wrap: {
            background: colors.surfaceDefault,
            borderColor: border,
        },
    },
};
const containedPrimaryButton = ({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "[data-dark] &": {
        backgroundColor: theme.palette.primary.contrastText,
        color: theme.palette.primary.main,
        "&:hover, &:focus, &:focus-visible": {
            backgroundColor: theme.palette.primary.contrastText,
            color: theme.palette.primary.main,
        },
    },
});
const containedSecondaryButton = ({ theme }) => ({
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
});
const outlinedPrimaryButton = ({ theme }) => ({
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    "[data-dark] &": {
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.contrastText,
    },
});
const outlinedSecondaryButton = ({ theme }) => ({
    color: theme.palette.secondary.main,
    backgroundColor: "transparent",
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
});
const containedErrorButton = ({ theme }) => ({
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
});
const outlinedErrorButton = ({ theme }) => ({
    color: theme.palette.error.main,
    backgroundColor: colors.surfaceDefault,
    borderColor: theme.palette.error.main,
    borderWidth: 2,
});
const textPrimaryButton = ({ theme }) => ({
    color: theme.palette.text.primary,
    "&:hover, &:focus, &:focus-visible": {
        backgroundColor: "transparent",
    },
    "[data-dark] &": {
        color: theme.palette.primary.contrastText,
    },
});
const textErrorButton = ({ theme }) => ({
    color: theme.palette.error.main,
});
const typography = {
    fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
    fontSize: 14,
    h1: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 700,
        fontSize: "28px",
        lineHeight: "120%",
        letterSpacing: "0.02em",
        "@media (min-width:1200px)": {
            fontSize: "36px",
        },
        "@media (min-width:1440px)": {
            fontSize: "40px",
        },
    },
    h2: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 700,
        fontSize: "20px",
        lineHeight: "130%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "28px", // Desktop up
        },
    },
    h3: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 700,
        fontSize: "18px",
        lineHeight: "130%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "24px", // Desktop up
        },
    },
    h4: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 700,
        fontSize: "16px",
        lineHeight: "130%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "24px", // Desktop up
        },
    },
    h5: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 700,
        fontSize: "16px",
        lineHeight: "135%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "18px", // Desktop up
        },
    },
    h6: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 700,
        fontSize: "14px",
        lineHeight: "140%",
        letterSpacing: "0.02em",
        "@media (min-width:600px)": {
            fontSize: "16px",
        },
    },
    subtitle1: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 600,
        fontSize: "14px",
        lineHeight: "150%",
        letterSpacing: "-0.02em",
        "@media (min-width:600px)": {
            fontSize: "16px",
        },
    },
    subtitle2: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 600,
        fontSize: "12px",
        lineHeight: "150%",
        "@media (min-width:600px)": {
            fontSize: "14px",
        },
    },
    body1: {
        fontSize: "14px",
        lineHeight: "160%",
        "@media (min-width:600px)": {
            fontSize: "16px",
        },
    },
    body2: {
        fontSize: "12px",
        lineHeight: "150%",
        "@media (min-width:600px)": {
            fontSize: "14px",
        },
    },
    button: {
        fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
        fontWeight: 600,
        fontSize: "14px",
        lineHeight: "150%",
        letterSpacing: "-0.02em",
        textTransform: "none",
        "@media (min-width:600px)": {
            fontSize: "16px",
        },
    },
    caption: {
        fontSize: "11px",
        lineHeight: "120%",
        "@media (min-width:600px)": {
            fontSize: "12px",
        },
    },
};
export default createTheme({
    colorSchemes: { dark: false },
    shape: {
        borderRadius: 0,
    },
    spacing: 8,
    palette: {
        primary,
        secondary,
        error,
        success,
        warning,
        info,
        border,
        additional,
        common,
        background: {
            default: colors.surfaceDefault,
            paper: colors.surfaceDefault,
            header: colors.brandBlue,
            footer: colors.brandBlue,
            main: colors.brandBlue,
            dark: colors.inkMain,
            grey: colors.surfaceGrey,
        },
        text: {
            primary: colors.inkMain,
            secondary: colors.inkMuted,
        },
    },
    typography,
    [overrideKey]: {
        Attachment: {
            container: {
                backgroundColor: colors.surfaceAttachment,
                "&:hover": {
                    backgroundColor: colors.surfaceAttachmentHover,
                },
            },
        },
    },
    components: {
        searchBarStyled: searchBarStyled,
        timeLineStepStyled: timeLineStepStyled,
        textStyled: textStyled,
        scrollableTabsStyled: scrollableTabsStyled,
        infoBlockStyled: infoBlockStyled,
        glossaryStyled: glossaryStyled,
        attachmentStyled: attachmentStyled,
        // évite le masquage de la scrollbar du body qui fait bouger le header
        MuiMenu: {
            defaultProps: {
                disableScrollLock: true,
            },
        },
        MuiLink: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.primary.main,
                    textDecorationColor: "currentColor",
                    fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
                    "&:hover": {
                        textDecoration: "underline",
                    },
                }),
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&.MuiAccordionSummary-root": {
                        padding: 0,
                        backgroundColor: "transparent",
                        color: theme.palette.primary.text,
                    },
                }),
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    fontFamily: "'Montserrat', 'Montserrat Fallback', sans-serif",
                    fontWeight: 600,
                    fontSize: "1rem",
                    lineHeight: "1.5rem",
                    letterSpacing: "-0.02em",
                    textTransform: "none",
                    borderRadius: 0,
                    minWidth: 72,
                    minHeight: 48,
                    padding: "12px 16px",
                    position: "relative",
                    // Permet l'animation du hover sur le label du bouton (underline)
                    "--button-label-padding-x": "16px",
                    "--button-label-bottom": "11px",
                    "--button-label-left": "var(--button-label-padding-x)",
                    "--button-label-right": "var(--button-label-padding-x)",
                    "&:has(.MuiButton-startIcon)": {
                        "--button-label-left": "calc(var(--button-label-padding-x) + 30px)",
                    },
                    "&:has(.MuiButton-endIcon)": {
                        "--button-label-right": "calc(var(--button-label-padding-x) + 30px)",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        right: "var(--button-label-right)",
                        bottom: "var(--button-label-bottom)",
                        left: "var(--button-label-left)",
                        height: "1px",
                        backgroundColor: "currentColor",
                        pointerEvents: "none",
                        transform: "scaleX(0)",
                        transformOrigin: "left",
                        transition: "transform 200ms ease",
                    },
                    gap: 12,
                    "&::before": {
                        display: "none",
                    },
                    "&:not(.Mui-disabled):hover::after": {
                        transform: "scaleX(1)",
                    },
                    "&.Mui-disabled": {
                        color: colors.textDisabled,
                        backgroundColor: colors.surfaceSection,
                        borderColor: colors.borderDisabled,
                    },
                },
                contained: {
                    border: "none",
                },
                outlined: {
                    boxSizing: "border-box",
                    padding: "10px 14px",
                    borderWidth: 2,
                    "--button-label-padding-x": "14px",
                    "--button-label-bottom": "9px",
                    "&.MuiButton-sizeLarge": {
                        padding: "14px 18px",
                        "--button-label-padding-x": "18px",
                        "--button-label-bottom": "13px",
                    },
                    "&.MuiButton-sizeSmall": {
                        padding: "6px 12px",
                        "--button-label-padding-x": "12px",
                        "--button-label-bottom": "5px",
                    },
                },
                containedError: containedErrorButton,
                sizeLarge: {
                    minHeight: 56,
                    padding: "16px 20px",
                    fontSize: "1rem",
                    "--button-label-padding-x": "20px",
                    "--button-label-bottom": "15px",
                },
                sizeMedium: {
                    minHeight: 48,
                    padding: "12px 16px",
                    fontSize: "15px",
                },
                sizeSmall: {
                    minHeight: 40,
                    padding: "10px 14px",
                    fontSize: "0.8125rem",
                    "--button-label-padding-x": "14px",
                    "--button-label-bottom": "9px",
                },
                containedPrimary: containedPrimaryButton,
                containedSecondary: containedSecondaryButton,
                outlinedPrimary: outlinedPrimaryButton,
                outlinedSecondary: outlinedSecondaryButton,
                outlinedError: outlinedErrorButton,
                textPrimary: textPrimaryButton,
                textError: textErrorButton,
                textInherit: {
                    color: primary.text,
                },
                startIcon: {
                    marginRight: 0,
                    marginLeft: 0,
                },
                endIcon: {
                    marginRight: 0,
                    marginLeft: 0,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "[data-dark] &": {
                        color: theme.palette.primary.contrastText,
                    },
                }),
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.primary,
                }),
                subtitle2: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
                body2: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
                caption: ({ theme }) => ({
                    color: theme.palette.text.secondary,
                }),
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.inkMain,
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    maxWidth: `1480px !important`,
                    paddingLeft: 0,
                    paddingRight: 0,
                    "@media (min-width:600px)": {
                        paddingLeft: 0,
                        paddingRight: 0,
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    outline: "1px solid",
                    outlineColor: theme.palette.border.default,
                    border: "none",
                    width: "fit-content !important",
                    height: "stretch",
                    justifySelf: "center",
                    "@media (max-width:900px)": {
                        width: "100% !important",
                    },
                }),
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: "32px !important",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: "260px",
                    maxWidth: "302px",
                    "@media (max-width:600px)": {
                        minHeight: "188px",
                        maxWidth: "100%",
                        justifyContent: "normal",
                        gap: "16px",
                        padding: "24px !important",
                    },
                    "& > .MuiBox-root": {
                        padding: 0,
                        "@media (max-width:600px)": {
                            padding: 0,
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 0,
                    boxShadow: "none",
                    "&.MuiAccordion-root": {
                        boxShadow: "none",
                        backgroundColor: "transparent",
                        color: theme.palette.text.secondary,
                        borderBottom: `1px solid ${colors.borderDefault}`,
                        ".MuiBox-root:has(> &.Da-Accordion)": {
                            marginBottom: "0 !important",
                        },
                        ".MuiBox-root:last-child > &.Da-Accordion": {
                            borderBottom: "none",
                        },
                    },
                }),
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "20px 24px",
                    gap: "10px",
                    minHeight: 58,
                    backgroundColor: colors.surfaceDefault,
                    borderBottom: `2px solid ${colors.inkMain}`,
                },
                input: {
                    padding: 0,
                },
                underline: {
                    "&::before": {
                        border: "none",
                    },
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "20px 24px",
                    gap: "10px",
                    minHeight: 58,
                    backgroundColor: colors.surfaceDefault,
                    borderBottom: `2px solid ${colors.inkMain}`,
                    "&:hover": {
                        backgroundColor: colors.surfaceDefault,
                    },
                    "&.Mui-focused": {
                        backgroundColor: colors.surfaceDefault,
                    },
                    "&.Mui-error": {
                        border: `1px solid ${error.main}`,
                        borderBottom: `1px solid ${error.main}`,
                        backgroundColor: colors.surfaceDefault,
                    },
                    "&.Mui-error.Mui-focused": {
                        border: `2px solid ${error.main}`,
                        borderBottom: `2px solid ${error.main}`,
                    },
                    "&::before": {
                        borderBottom: "none",
                    },
                    "&::after": {
                        borderBottom: "none",
                    },
                },
                input: {
                    padding: 0,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: "1rem",
                    minHeight: 26,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: colors.inkMain,
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontStyle: "normal",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "150%",
                    color: colors.black,
                    "&.Mui-error": {
                        color: error.main,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: "12px",
                    "@media (min-width:600px)": {
                        fontSize: "12px",
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                    marginTop: 6,
                    fontSize: "1rem",
                    lineHeight: "1.25rem",
                    "&.Mui-error": {
                        color: error.main,
                    },
                },
                contained: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: "100%",
                },
            },
        },
        MuiAccordionSummary: {
            defaultProps: {
                expandIcon: createElement(Icon, {
                    icon: "plus",
                    type: "far",
                    color: primary.text,
                    style: { fontSize: "18px" },
                }),
            },
            styleOverrides: {
                root: {
                    minHeight: 40,
                },
                expandIconWrapper: {
                    marginLeft: "21px",
                    transition: "transform 200ms cubic-bezier(0.2, 0, 0, 1)",
                    "&.Mui-expanded": {
                        transform: "rotate(45deg)",
                    },
                },
                content: {
                    marginTop: 28,
                    marginBottom: 28,
                    "&.Mui-expanded": {
                        marginTop: 28,
                        marginBottom: 16,
                    },
                    "@media (max-width:600px)": {
                        marginTop: 20,
                        marginBottom: 20,
                        "&.Mui-expanded": {
                            marginTop: 20,
                            marginBottom: 12,
                        },
                    },
                    "& .MuiTypography-root": {
                        fontWeight: 500,
                        fontSize: 20,
                        lineHeight: "135%",
                        letterSpacing: "-0.02em",
                        "@media (max-width:600px)": {
                            fontWeight: 600,
                            fontSize: 16,
                            lineHeight: "150%",
                            letterSpacing: "0em",
                        },
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    maxWidth: 900,
                    padding: "0 0 28px",
                    "@media (max-width:600px)": {
                        paddingBottom: "20px",
                    },
                    "& > *": {
                        padding: "0px !important",
                    },
                    "& p, & li, & span": {
                        "@media (min-width:600px)": {
                            fontSize: "16px !important",
                        },
                    },
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    maxWidth: "auto",
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                body: {
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    fontFamily: "'Roboto', sans-serif",
                    margin: 0,
                },
            }),
        },
    },
});
